import {Component, OnDestroy} from '@angular/core';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {ICellRendererParams} from 'ag-grid-community';
import {NumberUtils} from '../../../../shared-utilities/utils-old/shared-utils-old/number-utils';
import {DataAdditionalNumbers} from '../../../../shared-utilities/models-old/ngp-report-grid/data-additional-numbers';
import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {Store} from '@ngrx/store';
import {
  setEditedItem,
  setEditedItemsToStockPage,
  setPriceGpForStockManager,
} from '../../../../features/stock-manager/store/stock-manager.actions';
import {Observable, Subject} from 'rxjs';
import {StockItem} from '../../../../shared-utilities/models-old/datastructures';
import {NGPReport} from '../../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {formatThousands} from '../../../../shared-utilities/utils-old/formatting';
import {selectCurrentNavigationPage} from '../../../../features/user-access/user-stores-store/user-store.selectors';
import {takeUntil} from 'rxjs/operators';

/**
 * CellRendererCurrencyComponent renders custom HTML within an AG Grid Cell
 * The data manipulates numbers and displays a string formatted according to
 * additional data provided in the column definitions.
 */
@Component({
  selector: 'app-cell-renderer-currency',
  templateUrl: './cell-renderer-currency.component.html',
  styleUrls: ['./cell-renderer-currency.component.scss'],
  standalone: false,
})
export class CellRendererCurrencyComponent implements ICellRendererAngularComp, OnDestroy {

  /**
   * Todo: Add custom formatting for values from a setting
   * In example 4500.00 -> 4 500 | 4,500.00
   * Look at Date FNS - we can add custom formatting to all values
   */

  price: number | string = '';
  params: ICellRendererParams &
    { dataAdditional: DataAdditionalNumbers };

  currentPage$: Observable<string>;
  currentPage: string;
  protected readonly formatThousands = formatThousands;

  private destroy$ = new Subject<boolean>();

  constructor(
    private readonly store: Store,
  ) {
  }

  agInit(
    params: ICellRendererParams &
      { dataAdditional: DataAdditionalNumbers },
  ): void {
    this.params = params;
    this.price = +this.formatCurrency(+params?.value, 2);
    this.currentPage$ = this.store.select(selectCurrentNavigationPage);
    this.currentPage$.pipe(takeUntil(this.destroy$)).subscribe((page: string) => {
      this.currentPage = page;
    });

    if (isNaN(this.price) && (this.params.colDef.field === 'diffGP' || this.params.colDef.field === 'nominalGP')) {
      this.price = 100;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }


  refresh(params: ICellRendererParams): boolean {
    return true;
  }

  onUndoButtonClick(): void {
    this.params = {...this.params};
    this.params.data = structuredClone(this.params.data);
    this.price = this.params.data.originalValue[this.params.colDef.field].value;
    this.params.value = this.params.data.originalValue[this.params.colDef.field].value;
    this.params.data[this.params.colDef.field] = this.params.data.originalValue[this.params.colDef.field].value;
    if (this.params.colDef.field === 'sellPriIncl1' || this.params.colDef.field === 'nominalGP') {
      this.params.data.isEdited = Object.keys(this.params.data.originalValue).some((key: string) => {
        if (key !== 'isEdited' && key !== 'isSelected' && key !== 'error' && key !== 'newlyAdded' && key !== 'isError' && key !== 'nominalGP' && key !== 'sellPriIncl1') {
          const originalValue = this.params.data.originalValue[key].value;
          const currentValue = this.params.data[key];
          return originalValue !== currentValue;
        }
        return false;
      });
      if (this.currentPage === 'stock-manager') {
        this.params.data['sellPriIncl1'] = this.params.data.originalValue['sellPriIncl1'].value;
        this.params.data['nominalGP'] = this.params.data.originalValue['nominalGP'].value;
        this.store.dispatch(setPriceGpForStockManager({stockItem: this.params.data, field: this.params.colDef.field}));
      } else {
        this.params.data['diffGP'] = this.params.data.originalValue['diffGP'].value;
        this.params.data['sellPriIncl1'] = this.params.data.originalValue['sellPriIncl1'].value;
        this.params.data['nominalGP'] = this.params.data.originalValue['nominalGP'].value;

        void GridUtils.removeIsEditingFlagFromNgpItem(this.params.data as NGPReport, this.params.colDef.field, this.store);
        GridUtils.updateIsEditedAndForceRefresh(this.params);
      }
    } else {
      if (this.currentPage === 'stock-manager') {
        this.store.dispatch(setEditedItem({stockItem: this.params.data as StockItem}));
        this.store.dispatch(setEditedItemsToStockPage());
        GridUtils.updateIsEditedAndForceRefresh(this.params);
      } else {
        void GridUtils.removeIsEditingFlagFromNgpItem(this.params.data as NGPReport, this.params.colDef.field, this.store);
        GridUtils.updateIsEditedAndForceRefresh(this.params);
      }

    }


  }

  /**
   * Formats the value to display either a number value formatted according to the user settings.
   * If no value is provided it will format the value to a string.
   *
   * @param value The value that needs to be formatted.
   * @param decimalPlaces The number of decimals to be displayed after the number.
   * @returns If no value is provided - returns an empty string.
   * @returns If a value is provided - returns a formatted value according to the user settings.
   */
  formatCurrency(value: number | string, decimalPlaces: number): number | string {
    if (typeof value === 'string') {
      return '';
    }
    return NumberUtils.formatNumberToDecimals(+value, decimalPlaces);
  }
}
